import React, { Fragment, useState, useEffect } from "react";

import classnames from "classnames";
import Select from "react-select";
import { Controller, FormProvider, useForm } from "react-hook-form";
import DatePicker from "react-datepicker";
import axios from "axios";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  FormGroup,
  Label,
  Input,
  Form,
  FormFeedback,
} from "reactstrap";

import "eva-icons/style/eva-icons.css";
import CollapsibleSection from "./AddFacilitySidebarComponents/CollapsibleSection";
import TableNamesFields from "./AddFacilitySidebarComponents/TableNamesFields";

const defaultFormData = {
  facility: {
    name: "",
    emc_facility_name: "",
    emc_facility_id: "",
    emc_max_rating_1: "",
    emc_max_rating_2: ""
  },
  tableNames: [
    {
      asset_name: "",
      contractedCap: "",
      limitedRate: "",
      batMSB: "",
      batCap: "",
      chargeRate: "",
      can_script_change_state: 0,
      db_id: "",
    },
  ],
  tableBuildingParameter: {
    id: "",
    building_name: "",
    buildingPower_schedule: "",
    DRpower_adjustment: "",
    solar_fixed_price: "",
    target_price: "",
    hedgeScore_morning: "",
    hedgeScore_afternoon: "",
    hedgeScore_evening: "",
    hedgeRecord_date: "",
    hedge_tolerance: "",
    partake_hedge: 0,
    partake_peakShaveAmt: 0,
  }
};

const AddEventSidebar = (props) => {
  const {
    store,
    dispatch,
    open,
    handleAddFacility,
    refetchFacilities,
    addFacilityWithDetails,
    selectFacility,
    updateFacilityWithDetails,
  } = props;

  const methods = useForm({ defaultValues: defaultFormData });
  const { register, control, handleSubmit, errors, reset, getValues } = methods;
  const selectedFacility = store.selectedFacility;
  let [buildingPowerScheduleOptions, setBuildingPowerScheduleOptions] = useState([]);
  const [retrievedFormData, setRetrievedFormData] = useState(false);

  const isObjEmpty = (obj) => Object.keys(obj).length === 0;
  const isNumeric = (value) => {
    return /^\d*\.?\d*$/.test(value); // only positive int or float 
  };

  // turn empty field values to null
  const normaliseEmptyFields = (obj) => {
    if (Array.isArray(obj)) {
      obj.forEach((item) => normaliseEmptyFields(item));
    } else if (typeof obj === "object" && obj !== null) {
      Object.keys(obj).forEach((key) => {
        if (typeof obj[key] === "object" && obj[key] !== null) {
          normaliseEmptyFields(obj[key]);
        } else if (obj[key] === "") {
          obj[key] = null;
        }
      });
    }

    return obj;
  };

  const handleAddEvent = () => {
    const formData = normaliseEmptyFields(getValues());

    dispatch(addFacilityWithDetails(formData));
    refetchFacilities();
  };

  const handleSelectedEvent = async () => {
    if (!isObjEmpty(selectedFacility)) {
      setRetrievedFormData(false);
      const tableNamesResponse = await axios.get(`/table_names/facility/${selectedFacility.id}`);
      const tableNames = tableNamesResponse.data.map(item => {
        const { id, table_name, thing_name, ...rest } = item; // removes id for useFieldArray
        return {
          ...rest,
          asset_name: thing_name.slice(-4),
          db_id: id,
        };
      });

      const buildingName = tableNamesResponse.data[0].thing_name.slice(0, 6);
      const tableBuildingParametersResponse = await axios.get('/table_building_parameters', {
        params: { building_name: buildingName }
      });
      const tableBuildingParameter = tableBuildingParametersResponse.data || {};

      const formData = {
        facility: {
          name: selectedFacility.name || "",
          emc_facility_name: selectedFacility.emc_facility_name || "",
          emc_facility_id: selectedFacility.emc_facility_id || "",
          emc_max_rating_1: selectedFacility.emc_max_rating_1 || "",
          emc_max_rating_2: selectedFacility.emc_max_rating_2 || ""
        },
        tableNames: tableNames.map(item => ({
          ...item,
          can_script_change_state: item.can_script_change_state ?? 0,
        })),
        tableBuildingParameter: {
          ...tableBuildingParameter,
          partake_hedge: tableBuildingParameter?.partake_hedge ?? 0,
        },
      };

      reset(formData);
      setRetrievedFormData(true);
    }
  };

  const handleResetInputValues = () => {
    dispatch(selectFacility({}));
    reset(defaultFormData);
  };

  const handleUpdateEvent = () => {
    const formData = normaliseEmptyFields(getValues());
    const updatedFormData = {
      ...formData,
      facility: {
        ...formData.facility,
        id: selectedFacility.id
      },
    }

    dispatch(updateFacilityWithDetails(updatedFormData));
    handleAddFacility();
  };

  const EventActions = () => {
    if (
      isObjEmpty(selectedFacility) ||
      (!isObjEmpty(selectedFacility) && !selectedFacility.name.length)
    ) {
      return (
        <Fragment>
          <Button className="mr-3 btn-rounded" type="submit" color="primary">
            Add
          </Button>
          <Button
            className="btn-rounded"
            color="secondary"
            type="reset"
            onClick={handleAddFacility}
            outline
          >
            Cancel
          </Button>
        </Fragment>
      );
    } else {
      return (
        <Fragment>
          <Button
            className="mr-3 btn-rounded"
            color="primary"
            onClick={handleSubmit(() => {
              handleUpdateEvent();
              handleAddFacility();
            })}
          >
            Update
          </Button>
          <Button
            className="btn-rounded"
            color="secondary"
            type="reset"
            onClick={handleAddFacility}
            outline
          >
            Cancel
          </Button>
        </Fragment>
      );
    }
  };

  const CloseBtn = (
    <i className="eva eva-close cursor-pointer" onClick={handleAddFacility} />
  );

  useEffect(() => {
    const fetchBuildingPowerScheduleOptions = async () => {
      try {
        const response = await axios.get("/table_building_schedule");
        const options = response.data.map(x => {
          const schedule = x.buildingPower_schedule;
          const label = schedule[0].toUpperCase() + schedule.slice(1);
          return {
            value: schedule,
            label: label
          };
        });

        setBuildingPowerScheduleOptions(options);
      } catch (error) {
        console.error(error);
        const defaultOptions = [
          { value: "office", label: "Office" },
          { value: "datacenter", label: "Datacenter" },
          { value: "shopping", label: "Shopping" }
        ];

        setBuildingPowerScheduleOptions(defaultOptions);
      }
    };

    fetchBuildingPowerScheduleOptions();
  }, []);

  return (
    <Modal
      isOpen={open}
      toggle={handleAddFacility}
      onOpened={handleSelectedEvent}
      onClosed={handleResetInputValues}
      className="sidebar-lg"
      contentClassName="p-0"
      modalClassName="modal-slide-in event-sidebar"
    >
      <ModalHeader
        className="mb-1"
        toggle={handleAddFacility}
        close={CloseBtn}
        tag="div"
      >
        <h5 className="modal-title">
          {isObjEmpty(selectedFacility) ||
            (!isObjEmpty(selectedFacility) &&
              !selectedFacility.name?.length)
            ? "Add"
            : "Edit"}{" "}
          Facility
        </h5>
      </ModalHeader>
      <ModalBody className="flex-grow-1 pb-sm-0 pb-3">
        <FormProvider {...methods}>
          <Form onSubmit={handleSubmit((data) => {
            if (isObjEmpty(selectedFacility) || !selectedFacility.title?.length) {
              handleAddEvent();
            } else {
              handleUpdateEvent();
            }
            handleAddFacility();
          })}>
            <CollapsibleSection title="Facility details">
              <FormGroup>
                <Label for="name">
                  Name <span className="text-danger">*</span>
                </Label>
                <Input
                  id="name"
                  name="facility.name"
                  placeholder="e.g. City Hall"
                  innerRef={register({
                    required: true,
                  })}
                  className={classnames({
                    "is-invalid": errors.facility?.name,
                  })}
                />
              </FormGroup>
              <FormGroup>
                <Label for="emc_facility_name">
                  EMC Facility Name
                </Label>
                <Input
                  id="emc_facility_name"
                  name="facility.emc_facility_name"
                  innerRef={register()}
                />
              </FormGroup>
              <FormGroup>
                <Label for="emc_facility_id">
                  EMC Facility ID
                </Label>
                <Input
                  id="emc_facility_id"
                  name="facility.emc_facility_id"
                  innerRef={register()}
                />
              </FormGroup>
              <FormGroup style={{ display: 'flex', gap: '1rem' }}>
                <div>
                  <Label for="emc_max_rating_1">
                    IL Max Rating
                  </Label>
                  <Input
                    id="emc_max_rating_1"
                    name="facility.emc_max_rating_1"
                    innerRef={register({
                      validate: (value) => isNumeric(value),
                    })}
                    className={classnames({
                      "is-invalid": errors.facility?.emc_max_rating_1,
                    })}
                  />
                  {errors.facility?.emc_max_rating_1 && (
                    <FormFeedback>Must be a valid number</FormFeedback>
                  )}
                </div>
                <div>
                  <Label for="emc_max_rating_2">
                    DR Max Rating
                  </Label>
                  <Input
                    id="emc_max_rating_2"
                    name="facility.emc_max_rating_2"
                    innerRef={register({
                      validate: (value) => isNumeric(value),
                    })}
                    className={classnames({
                      "is-invalid": errors.facility?.emc_max_rating_2,
                    })}
                  />
                  {errors.facility?.emc_max_rating_2 && (
                    <FormFeedback>Must be a valid number</FormFeedback>
                  )}
                </div>
              </FormGroup>
              <FormGroup style={{ display: 'flex', gap: '1rem' }}>
                <div>
                  <Label for="emc_ramp_up">
                    Ramp Up Rate
                  </Label>
                  <Input
                    id="emc_ramp_up"
                    name="facility.emc_ramp_up"
                    innerRef={register({
                      validate: (value) => isNumeric(value),
                    })}
                    className={classnames({
                      "is-invalid": errors.facility?.emc_ramp_up,
                    })}
                  />
                  {errors.facility?.emc_ramp_up && (
                    <FormFeedback>Must be a valid number</FormFeedback>
                  )}
                </div>
                <div>
                  <Label for="emc_ramp_down">
                    Ramp Down Rate
                  </Label>
                  <Input
                    id="emc_ramp_down"
                    name="facility.emc_ramp_down"
                    innerRef={register({
                      validate: (value) => isNumeric(value),
                    })}
                    className={classnames({
                      "is-invalid": errors.facility?.emc_ramp_down,
                    })}
                  />
                  {errors.facility?.emc_ramp_down && (
                    <FormFeedback>Must be a valid number</FormFeedback>
                  )}
                </div>
              </FormGroup>
            </CollapsibleSection>
            <CollapsibleSection title="Table Building Parameters details">
              <Input
                type="hidden"
                id="table_building_parameter_id"
                name="tableBuildingParameter.id"
                innerRef={register()}
              />
              <FormGroup style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <Label for="building_name">
                    Building Name <span className="text-danger">*</span>
                  </Label>
                  <Input
                    id="building_name"
                    name="tableBuildingParameter.building_name"
                    placeholder="pyc001"
                    innerRef={register({ required: true })}
                    className={classnames({
                      "is-invalid": errors.tableBuildingParameter?.building_name,
                    })}
                  />
                  {errors.tableBuildingParameter?.building_name && (
                    <FormFeedback>{errors.tableBuildingParameter?.building_name.message}</FormFeedback>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <Label for="buildingPower_schedule">
                    Building Power Schedule <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    name="tableBuildingParameter.buildingPower_schedule"
                    control={control}
                    defaultValue={buildingPowerScheduleOptions[0]?.value || ""}
                    rules={{ required: true }}
                    render={(field) => (
                      <Select
                        id="buildingPower_schedule"
                        options={buildingPowerScheduleOptions}
                        value={buildingPowerScheduleOptions.find(option => option.value === field.value) || null}
                        onChange={option => field.onChange(option.value)}
                        classNamePrefix="react-select"
                        className={classnames({
                          "is-invalid": errors.tableBuildingParameter?.buildingPower_schedule,
                        })}
                      />
                    )}
                  />
                  {errors.tableBuildingParameter?.buildingPower_schedule && (
                    <FormFeedback>Required</FormFeedback>
                  )}
                </div>
              </FormGroup>
              <FormGroup style={{ display: 'flex', gap: '1rem' }}>
                <div>
                  <Label for="DRpower_adjustment">
                    DR Power Adjustment
                  </Label>
                  <Input
                    id="DRpower_adjustment"
                    name="tableBuildingParameter.DRpower_adjustment"
                    innerRef={register({
                      validate: (value) => isNumeric(value),
                    })}
                    className={classnames({
                      "is-invalid": errors.tableBuildingParameter?.DRpower_adjustment,
                    })}
                  />
                  {errors.tableBuildingParameter?.DRpower_adjustment && (
                    <FormFeedback>Must be a valid number</FormFeedback>
                  )}
                </div>
                <div>
                  <Label for="solar_fixed_price">
                    Solar Fixed Price
                  </Label>
                  <Input
                    id="solar_fixed_price"
                    name="tableBuildingParameter.solar_fixed_price"
                    innerRef={register({
                      validate: (value) => isNumeric(value),
                    })}
                    className={classnames({
                      "is-invalid": errors.tableBuildingParameter?.solar_fixed_price,
                    })}
                  />
                  {errors.tableBuildingParameter?.solar_fixed_price && (
                    <FormFeedback>Must be a valid number</FormFeedback>
                  )}
                </div>
              </FormGroup>
              <FormGroup style={{ display: 'flex', gap: '1rem' }}>
                <div>
                  <Label for="target_price">
                    Target Price
                  </Label>
                  <Input
                    id="target_price"
                    name="tableBuildingParameter.target_price"
                    innerRef={register({
                      validate: (value) => isNumeric(value),
                    })}
                    className={classnames({
                      "is-invalid": errors.tableBuildingParameter?.target_price,
                    })}
                  />
                  {errors.tableBuildingParameter?.target_price && (
                    <FormFeedback>Must be a valid number</FormFeedback>
                  )}
                </div>
                <div>
                  <Label for="hedgeScore_morning">
                    Hedge Score Morning
                  </Label>
                  <Input
                    id="hedgeScore_morning"
                    name="tableBuildingParameter.hedgeScore_morning"
                    innerRef={register({
                      validate: (value) => isNumeric(value),
                    })}
                    className={classnames({
                      "is-invalid": errors.tableBuildingParameter?.hedgeScore_morning,
                    })}
                  />
                  {errors.tableBuildingParameter?.hedgeScore_morning && (
                    <FormFeedback>Must be a valid number</FormFeedback>
                  )}
                </div>
              </FormGroup>
              <FormGroup style={{ display: 'flex', gap: '1rem' }}>
                <div>
                  <Label for="hedgeScore_afternoon">
                    Hedge Score Afternoon
                  </Label>
                  <Input
                    id="hedgeScore_afternoon"
                    name="tableBuildingParameter.hedgeScore_afternoon"
                    innerRef={register({
                      validate: (value) => isNumeric(value),
                    })}
                    className={classnames({
                      "is-invalid": errors.tableBuildingParameter?.hedgeScore_afternoon,
                    })}
                  />
                  {errors.tableBuildingParameter?.hedgeScore_afternoon && (
                    <FormFeedback>Must be a valid number</FormFeedback>
                  )}
                </div>
                <div>
                  <Label for="hedgeScore_evening">
                    Hedge Score Evening
                  </Label>
                  <Input
                    id="hedgeScore_evening"
                    name="tableBuildingParameter.hedgeScore_evening"
                    innerRef={register({
                      validate: (value) => isNumeric(value),
                    })}
                    className={classnames({
                      "is-invalid": errors.tableBuildingParameter?.hedgeScore_evening,
                    })}
                  />
                  {errors.tableBuildingParameter?.hedgeScore_evening && (
                    <FormFeedback>Must be a valid number</FormFeedback>
                  )}
                </div>
              </FormGroup>
              <FormGroup style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <Label for="hedgeRecord_date">
                    Hedge Record Date
                  </Label>
                  <Controller
                    control={control}
                    name="tableBuildingParameter.hedgeRecord_date"
                    defaultValue={null}
                    render={({ onChange, value }) => (
                      <DatePicker
                        id="hedgeRecord_date"
                        selected={value ? new Date(value) : null}
                        onChange={onChange}
                        dateFormat="dd/MM/yyyy"
                        className="form-control"
                        placeholderText="Select date"
                      />
                    )}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <Label for="hedge_tolerance">
                    Hedge Tolerance
                  </Label>
                  <Input
                    id="hedge_tolerance"
                    name="tableBuildingParameter.hedge_tolerance"
                    placeholder="e.g. [0.1,0.1,0.05]"
                    innerRef={register({
                      validate: (value) => {
                        if (!value) return true;
                        return /^\[(\d+(\.\d+)?)(,\d+(\.\d+)?)*\]$/.test(value)
                          ? true
                          : "Must be in this format: [0.1,0.1,0.05]";
                      },
                    })}
                    className={classnames({
                      "is-invalid": errors.tableBuildingParameter?.hedge_tolerance,
                    })}
                  />
                  {errors.tableBuildingParameter?.hedge_tolerance && (
                    <FormFeedback>{errors.tableBuildingParameter.hedge_tolerance.message}</FormFeedback>
                  )}
                </div>
              </FormGroup>
              <FormGroup style={{ display: "flex", gap: "0.5rem" }}>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                  <Label for="partake_peakShaveAmt">
                    Partake Peak Shave Amt
                  </Label>
                </div>
                <div style={{ flex: 1 }}>
                  <Input
                    id="partake_peakShaveAmt"
                    name="tableBuildingParameter.partake_peakShaveAmt"
                    innerRef={register({
                      validate: (value) => isNumeric(value),
                    })}
                    className={classnames({
                      "is-invalid": errors.tableBuildingParameter?.partake_peakShaveAmt,
                    })}
                  />
                </div>
                {errors.tableBuildingParameter?.partake_peakShaveAmt && (
                  <FormFeedback>Must be a valid number</FormFeedback>
                )}
              </FormGroup>
              <FormGroup check>
                <Controller
                  name="tableBuildingParameter.partake_hedge"
                  control={control}
                  defaultValue={0}
                  render={({ value, onChange }) => (
                    <Input
                      type="checkbox"
                      id="partake_hedge"
                      checked={!!value}
                      onChange={(e) => onChange(e.target.checked ? 1 : 0)}
                    />
                  )}
                />
                <Label for="partake_hedge" check>
                  Partake in hedge?
                </Label>
              </FormGroup>
            </CollapsibleSection>
            <CollapsibleSection title="Table Names details">
              {(retrievedFormData || isObjEmpty(selectedFacility)) && (
                <TableNamesFields isNumeric={isNumeric} />
              )}
            </CollapsibleSection>
            <FormGroup className="d-flex">
              <EventActions />
            </FormGroup>
          </Form>
        </FormProvider>
      </ModalBody>
    </Modal>
  );
};

export default AddEventSidebar;
